/**
 * FeatureFlagsTab — site-wide feature toggles (site_config/feature_flags).
 * Extracted verbatim from AdminScreen.jsx (god-file decomposition).
 */
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { db } from '../firebaseConfig';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { Ionicons } from '@expo/vector-icons';
import { showToast } from '../utils/toast';
import { adminStyles as styles } from './adminStyles';
import { uiVar } from './floodlight/uiStyleCss';
import { ADM_DISPLAY } from './floodlight/vars/admin';

export default function FeatureFlagsTab() {
  const [introSequenceEnabled, setIntroSequenceEnabled] = useState(false);
  const [communityScreenEnabled, setCommunityScreenEnabled] = useState(false);
  // Default the DFS exclusion ON — matches backend default (getFlag(..., true)).
  // Admins can flip it off here if a client specifically wants DFS/sweeps/
  // aggregator books contributing to consensus math again (rare; generally
  // makes +EV numbers look bigger and less trustworthy).
  const [dfsConsensusExclusionEnabled, setDfsConsensusExclusionEnabled] = useState(true);
  // v1.16.7 — one-sided juice policy: 'market_typical' | 'fixed_10pct'
  const [oneSidedJuicePolicy, setOneSidedJuicePolicy] = useState('market_typical');
  const [newResultsUiEnabled, setNewResultsUiEnabled] = useState(false);
  const [newUiShowEverywhere, setNewUiShowEverywhere] = useState(false);
  const [sgpMathV2Enabled, setSgpMathV2Enabled] = useState(true);
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [introSaving, setIntroSaving] = useState(false);
  const [communitySaving, setCommunitySaving] = useState(false);
  const [dfsSaving, setDfsSaving] = useState(false);
  const [juiceSaving, setJuiceSaving] = useState(false);
  const [newUiSaving, setNewUiSaving] = useState(false);
  const [newUiScopeSaving, setNewUiScopeSaving] = useState(false);
  const [sgpMathSaving, setSgpMathSaving] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'site_config', 'feature_flags'),
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        setIntroSequenceEnabled(d.introSequenceEnabled === true);
        setCommunityScreenEnabled(d.communityScreenEnabled === true);
        // Absence === enabled (backend default is true). Only flip to false
        // when explicitly set to false in Firestore.
        setDfsConsensusExclusionEnabled(d.dfsConsensusExclusionEnabled !== false);
        // v1.16.7 — juice policy. Default 'market_typical' when unset.
        setOneSidedJuicePolicy(d.oneSidedJuicePolicy === 'fixed_10pct' ? 'fixed_10pct' : 'market_typical');
        setNewResultsUiEnabled(d.newResultsUiEnabled === true);
        setNewUiShowEverywhere(d.newUiShowEverywhere === true);
        setSgpMathV2Enabled(d.sgpMathV2Enabled !== false);
        setFlagsLoading(false);
      },
      () => setFlagsLoading(false)
    );
    return () => unsub();
  }, []);

  const toggleIntroSequence = async () => {
    const next = !introSequenceEnabled;
    setIntroSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'feature_flags'), { introSequenceEnabled: next }, { merge: true });
      showToast(next ? 'Intro sequence enabled for new loads' : 'Intro sequence disabled', 'success');
    } catch (e) {
      showToast('Save failed: ' + (e?.message || String(e)), 'error');
    } finally {
      setIntroSaving(false);
    }
  };

  const toggleCommunityScreen = async () => {
    const next = !communityScreenEnabled;
    setCommunitySaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'feature_flags'), { communityScreenEnabled: next }, { merge: true });
      showToast(next ? 'Community screen enabled — nav goes to full community page' : 'Community screen disabled — waitlist modal shown', 'success');
    } catch (e) {
      showToast('Save failed: ' + (e?.message || String(e)), 'error');
    } finally {
      setCommunitySaving(false);
    }
  };

  const toggleDfsConsensusExclusion = async () => {
    const next = !dfsConsensusExclusionEnabled;
    setDfsSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'feature_flags'), { dfsConsensusExclusionEnabled: next }, { merge: true });
      showToast(
        next
          ? 'DFS/sweeps/aggregators EXCLUDED from consensus math (default, cleaner EV%)'
          : 'DFS/sweeps/aggregators INCLUDED in consensus math (legacy behavior, noisier EV%)',
        'success'
      );
    } catch (e) {
      showToast('Save failed: ' + (e?.message || String(e)), 'error');
    } finally {
      setDfsSaving(false);
    }
  };

  const toggleNewResultsUi = async () => {
    const next = !newResultsUiEnabled;
    setNewUiSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'feature_flags'), { newResultsUiEnabled: next }, { merge: true });
      showToast(next ? 'New card UI enabled — visible on staging/dev (scope toggle controls prod)' : 'New card UI disabled — classic layout shown everywhere', 'success');
    } catch (e) {
      showToast('Save failed: ' + (e?.message || String(e)), 'error');
    } finally {
      setNewUiSaving(false);
    }
  };

  const toggleNewUiScope = async () => {
    const next = !newUiShowEverywhere;
    setNewUiScopeSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'feature_flags'), { newUiShowEverywhere: next }, { merge: true });
      showToast(
        next
          ? 'New card UI rolled out to ALL users (production)'
          : 'New card UI limited to staging & dev only',
        'success'
      );
    } catch (e) {
      showToast('Save failed: ' + (e?.message || String(e)), 'error');
    } finally {
      setNewUiScopeSaving(false);
    }
  };

  // v1.16.7 — toggle one-sided juice policy (market_typical ↔ fixed_10pct).
  // Flip is symmetric: tapping the toggle switches between the two policies.
  const toggleOneSidedJuicePolicy = async () => {
    const next = oneSidedJuicePolicy === 'market_typical' ? 'fixed_10pct' : 'market_typical';
    setJuiceSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'feature_flags'), { oneSidedJuicePolicy: next }, { merge: true });
      showToast(
        next === 'market_typical'
          ? 'One-sided fallback juice: MARKET-TYPICAL (per-market calibrated, default)'
          : 'One-sided fallback juice: FIXED 10% (legacy flat assumption)',
        'success'
      );
    } catch (e) {
      showToast('Save failed: ' + (e?.message || String(e)), 'error');
    } finally {
      setJuiceSaving(false);
    }
  };

  const toggleSgpMathV2 = async () => {
    const next = !sgpMathV2Enabled;
    setSgpMathSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'feature_flags'), { sgpMathV2Enabled: next }, { merge: true });
      showToast(
        next
          ? 'SGP math v2 ON — same-book legs, cache heal'
          : 'SGP math v2 OFF — cross-book legs, no cache heal',
        'success'
      );
    } catch (e) {
      showToast('Save failed: ' + (e?.message || String(e)), 'error');
    } finally {
      setSgpMathSaving(false);
    }
  };

  // Reusable flag card (inline — closes over flagsLoading)
  const FlagCard = ({ icon, category, categoryColor, categoryBg, title, field, isOn, isSaving, onToggle, descOn, descOff, impactOn, impactOff, defaultLabel }) => {
    const saving = flagsLoading || isSaving;
    return (
      <View style={ffStyles.card}>
        <View style={ffStyles.cardHeader}>
          <View style={[ffStyles.categoryBadge, { backgroundColor: categoryBg }]}>
            <Ionicons name={icon} size={11} color={categoryColor} />
            <Text style={[ffStyles.categoryText, { color: categoryColor }]}>{category}</Text>
          </View>
          <View style={[ffStyles.statusPill, isOn ? ffStyles.statusPillOn : ffStyles.statusPillOff]}>
            <View style={[ffStyles.statusDot, isOn ? ffStyles.statusDotOn : ffStyles.statusDotOff]} />
            <Text style={[ffStyles.statusText, isOn ? ffStyles.statusTextOn : ffStyles.statusTextOff]}>
              {isOn ? 'ON' : 'OFF'}
            </Text>
          </View>
        </View>

        <Text style={ffStyles.cardTitle}>{title}</Text>

        <View style={ffStyles.fieldRow}>
          <Ionicons name="code-slash-outline" size={10} color="rgba(255,255,255,0.3)" />
          <Text style={ffStyles.fieldName}>{field}</Text>
          {defaultLabel && <Text style={ffStyles.defaultLabel}>{defaultLabel}</Text>}
        </View>

        <View style={ffStyles.divider} />

        <View style={[ffStyles.stateBlock, isOn ? ffStyles.stateBlockOn : ffStyles.stateBlockOff]}>
          <Text style={[ffStyles.stateLabel, isOn ? ffStyles.stateLabelOn : ffStyles.stateLabelOff]}>
            {isOn ? 'ACTIVE' : 'INACTIVE'}
          </Text>
          <Text style={ffStyles.stateDesc}>{isOn ? descOn : descOff}</Text>
        </View>

        <Text style={ffStyles.impactText}>{isOn ? impactOn : impactOff}</Text>

        <TouchableOpacity
          onPress={saving ? undefined : onToggle}
          activeOpacity={0.85}
          style={[ffStyles.toggleRow, isOn ? ffStyles.toggleRowOn : ffStyles.toggleRowOff]}
        >
          <Text style={[ffStyles.toggleLabel, isOn ? ffStyles.toggleLabelOn : ffStyles.toggleLabelOff]}>
            {saving ? 'Saving…' : isOn ? 'Turn OFF' : 'Turn ON'}
          </Text>
          {saving ? (
            <ActivityIndicator size="small" color={isOn ? '#4ade80' : 'rgba(255,255,255,0.5)'} />
          ) : (
            <View style={[ffStyles.togglePill, isOn ? ffStyles.togglePillOn : ffStyles.togglePillOff]}>
              <View style={[ffStyles.toggleThumbInner, isOn ? ffStyles.toggleThumbInnerOn : ffStyles.toggleThumbInnerOff]} />
            </View>
          )}
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={styles.tabWrap}>
      <View style={ffStyles.pageHeader}>
        <View style={{ flex: 1 }}>
          <Text style={styles.sectionTitle}>Feature Flags</Text>
          <Text style={[styles.caption, { marginTop: 2, marginBottom: 0 }]}>
            Live Firestore switches — changes apply immediately to all users with no deploy required.
          </Text>
        </View>
        <View style={ffStyles.docChip}>
          <Ionicons name="server-outline" size={11} color="rgba(255,255,255,0.4)" />
          <Text style={ffStyles.docChipText}>site_config / feature_flags</Text>
        </View>
      </View>

      <View style={ffStyles.grid}>

        <FlagCard
          icon="film-outline"
          category="Experience"
          categoryColor="#a78bfa"
          categoryBg="rgba(139,92,246,0.15)"
          title="Intro Sequence"
          field="introSequenceEnabled"
          defaultLabel="default: OFF"
          isOn={introSequenceEnabled}
          isSaving={introSaving}
          onToggle={toggleIntroSequence}
          descOn={'Cinematic "BETOPTIMIZED" marquee and curtain animation plays on every full page load before the app is shown.'}
          descOff={'Users land directly in the app after fonts finish loading. No animation, no delay.'}
          impactOn={'⚡ Adds ~2–3s to perceived load time. Best for launch events or marketing pushes.'}
          impactOff={'✅ Recommended default — fastest time-to-interactive for returning users.'}
        />

        <FlagCard
          icon="people-outline"
          category="Navigation"
          categoryColor="#38bdf8"
          categoryBg="rgba(56,189,248,0.12)"
          title="Community Screen"
          field="communityScreenEnabled"
          defaultLabel="default: OFF"
          isOn={communityScreenEnabled}
          isSaving={communitySaving}
          onToggle={toggleCommunityScreen}
          descOn={'Community nav link routes to the full Discord-style community page with posts, threads, and member activity.'}
          descOff={'Community nav button shows the waitlist modal or redirect URL. Full page is hidden from all users.'}
          impactOn={'⚡ Community page must be production-ready and seeded with content before enabling.'}
          impactOff={'✅ Safe default — prevents users from landing on an empty work-in-progress feed.'}
        />

        {/* ── New Card UI — single card with two inline toggles ── */}
        <View style={ffStyles.card}>
          <View style={ffStyles.cardHeader}>
            <View style={[ffStyles.categoryBadge, { backgroundColor: 'rgba(251,146,60,0.12)' }]}>
              <Ionicons name="sparkles-outline" size={11} color="#fb923c" />
              <Text style={[ffStyles.categoryText, { color: '#fb923c' }]}>UI / Beta</Text>
            </View>
          </View>

          <Text style={ffStyles.cardTitle}>New Card UI (v2)</Text>

          <View style={ffStyles.fieldRow}>
            <Ionicons name="code-slash-outline" size={10} color="rgba(255,255,255,0.3)" />
            <Text style={ffStyles.fieldName}>newResultsUiEnabled · newUiShowEverywhere</Text>
            <Text style={ffStyles.defaultLabel}>default: both OFF</Text>
          </View>

          <View style={ffStyles.divider} />

          {/* Toggle 1 — master on/off */}
          <TouchableOpacity
            onPress={(flagsLoading || newUiSaving) ? undefined : toggleNewResultsUi}
            activeOpacity={0.85}
            style={[ffStyles.toggleRow, newResultsUiEnabled ? ffStyles.toggleRowOn : ffStyles.toggleRowOff]}
          >
            <View style={{ flex: 1 }}>
              <Text style={[ffStyles.toggleLabel, newResultsUiEnabled ? ffStyles.toggleLabelOn : ffStyles.toggleLabelOff]}>
                {(flagsLoading || newUiSaving) ? 'Saving…' : newResultsUiEnabled ? 'New UI ON' : 'New UI OFF'}
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 2 }}>
                {newResultsUiEnabled ? 'New card layout active (scope below controls who sees it)' : 'Classic layout shown everywhere — toggle on to enable v2'}
              </Text>
            </View>
            {(flagsLoading || newUiSaving) ? (
              <ActivityIndicator size="small" color={newResultsUiEnabled ? '#4ade80' : 'rgba(255,255,255,0.5)'} />
            ) : (
              <View style={[ffStyles.togglePill, newResultsUiEnabled ? ffStyles.togglePillOn : ffStyles.togglePillOff]}>
                <View style={[ffStyles.toggleThumbInner, newResultsUiEnabled ? ffStyles.toggleThumbInnerOn : ffStyles.toggleThumbInnerOff]} />
              </View>
            )}
          </TouchableOpacity>

          {/* Toggle 2 — scope (only meaningful when master is ON) */}
          <TouchableOpacity
            onPress={(flagsLoading || newUiScopeSaving || !newResultsUiEnabled) ? undefined : toggleNewUiScope}
            activeOpacity={newResultsUiEnabled ? 0.85 : 1}
            style={[
              ffStyles.toggleRow,
              newUiShowEverywhere ? ffStyles.toggleRowOn : ffStyles.toggleRowOff,
              !newResultsUiEnabled && { opacity: 0.4 },
              { marginTop: 6 },
            ]}
          >
            <View style={{ flex: 1 }}>
              <Text style={[ffStyles.toggleLabel, newUiShowEverywhere ? ffStyles.toggleLabelOn : ffStyles.toggleLabelOff]}>
                {(flagsLoading || newUiScopeSaving) ? 'Saving…' : newUiShowEverywhere ? 'All environments' : 'Staging & dev only'}
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 2 }}>
                {newUiShowEverywhere
                  ? 'New UI shown to all production users'
                  : 'New UI limited to localhost / staging — prod users see classic layout'}
              </Text>
            </View>
            {(flagsLoading || newUiScopeSaving) ? (
              <ActivityIndicator size="small" color={newUiShowEverywhere ? '#4ade80' : 'rgba(255,255,255,0.5)'} />
            ) : (
              <View style={[ffStyles.togglePill, newUiShowEverywhere ? ffStyles.togglePillOn : ffStyles.togglePillOff]}>
                <View style={[ffStyles.toggleThumbInner, newUiShowEverywhere ? ffStyles.toggleThumbInnerOn : ffStyles.toggleThumbInnerOff]} />
              </View>
            )}
          </TouchableOpacity>
        </View>

        <FlagCard
          icon="calculator-outline"
          category="Math / EV"
          categoryColor="#4ade80"
          categoryBg="rgba(74,222,128,0.1)"
          title="DFS / Sweeps Exclusion"
          field="dfsConsensusExclusionEnabled"
          defaultLabel="default: ON"
          isOn={dfsConsensusExclusionEnabled}
          isSaving={dfsSaving}
          onToggle={toggleDfsConsensusExclusion}
          descOn={"PrizePicks, Underdog, Fliff, Sportzino, Rebet, Blue Book, and regional-mirror books are excluded from the consensus fair-value calculation. Prices still appear on cards — they just don't influence the market probability used for EV%."}
          descOff={"DFS/pick'em, sweepstakes, prop aggregators, and mirrors are included in consensus math. Inflates the 'market' probability with high-vig books."}
          impactOn={'✅ Cleaner, more honest EV%. Consensus is not skewed by books running 30–50% vig.'}
          impactOff={'⚠️ EV% will appear larger and noisier. Only use to match pre-exclusion baseline numbers.'}
        />

        <FlagCard
          icon="git-branch-outline"
          category="Math / EV"
          categoryColor="#4ade80"
          categoryBg="rgba(74,222,128,0.1)"
          title="SGP Math v2"
          field="sgpMathV2Enabled"
          defaultLabel="default: ON"
          isOn={sgpMathV2Enabled}
          isSaving={sgpMathSaving}
          onToggle={toggleSgpMathV2}
          descOn={'New SGP pipeline: same-book leg decimal for R baseline, and cache read heal for historical clamped docs. (R has no floor in either mode.)'}
          descOff={'Cross-book leg product for the R baseline, no cache heal/rescale. R still has no floor.'}
          impactOn={'✅ Default. Same-book leg pricing makes EV% drop on wide same-player SGP spreads (e.g. Dort-class combos), which is the correct behavior.'}
          impactOff={'⚠️ Reverts to cross-book leg pricing and skips cache healing, so EV% on affected SGPs can read higher. Use only to compare against old numbers.'}
        />

        <FlagCard
          icon="scale-outline"
          category="Math / EV"
          categoryColor="#4ade80"
          categoryBg="rgba(74,222,128,0.1)"
          title="Market-Typical Juice"
          field="oneSidedJuicePolicy"
          defaultLabel={`current: ${oneSidedJuicePolicy === 'market_typical' ? 'MARKET-TYPICAL' : 'FIXED 10%'}`}
          isOn={oneSidedJuicePolicy === 'market_typical'}
          isSaving={juiceSaving}
          onToggle={toggleOneSidedJuicePolicy}
          descOn={"When a market has no real opposite side, the fabricated vig is calibrated per market type: 3.5% for thin alt props, 4–4.5% for spreads, 5–6% for prop mains, up to 10% for 3-way moneylines. Accurate because a 'Player Over 0.5' line genuinely has ~3% vig, not 10%."}
          descOff={"Every one-sided market gets a flat 10% assumed vig regardless of type. Legacy pre-v1.16.7 default. Inflates EV on near-certain markets like Player Over 0.5 assists."}
          impactOn={'✅ Default since v1.16.7 — recommended for all environments.'}
          impactOff={'⚠️ Legacy only. Use to reproduce pre-v1.16.7 EV numbers for comparison.'}
        />

      </View>

      <View style={ffStyles.footer}>
        <Ionicons name="information-circle-outline" size={13} color="rgba(255,255,255,0.3)" />
        <Text style={ffStyles.footerText}>
          All flags write to{' '}
          <Text style={ffStyles.footerCode}>site_config / feature_flags</Text>
          {' '}and propagate via Firestore real-time listeners within ~1–2 seconds. No server restart required.
        </Text>
      </View>
    </View>
  );
}

// ─── Feature flags card styles ────────────────────────────────────────────────
const ffStyles = StyleSheet.create({
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 12,
    flexWrap: 'wrap',
  },
  docChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    marginTop: 2,
  },
  docChipText: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 11,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  card: {
    // 3 columns on wide screens, 2 on medium, 1 on narrow
    flexBasis: Platform.OS === 'web' ? 'calc(33.333% - 8px)' : '100%',
    minWidth: 240,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    padding: 16,
    gap: 10,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  categoryBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  categoryText: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 20,
    borderWidth: 1,
  },
  statusPillOn: {
    backgroundColor: 'rgba(74,222,128,0.1)',
    borderColor: 'rgba(74,222,128,0.3)',
  },
  statusPillOff: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderColor: 'rgba(255,255,255,0.1)',
  },
  statusDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
  },
  statusDotOn: { backgroundColor: '#4ade80' },
  statusDotOff: { backgroundColor: 'rgba(255,255,255,0.25)' },
  statusText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
  statusTextOn: { color: '#4ade80' },
  statusTextOff: { color: 'rgba(255,255,255,0.4)' },
  cardTitle: { ...ADM_DISPLAY,
    color: uiVar('adm-ink', '#fff'),
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: -0.2,
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    flexWrap: 'wrap',
  },
  fieldName: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 11,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier',
  },
  defaultLabel: {
    color: 'rgba(255,255,255,0.2)',
    fontSize: 10,
    fontStyle: 'italic',
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.06)',
    marginVertical: 2,
  },
  stateBlock: {
    borderRadius: 8,
    padding: 10,
    gap: 4,
    borderWidth: 1,
  },
  stateBlockOn: {
    backgroundColor: 'rgba(74,222,128,0.05)',
    borderColor: 'rgba(74,222,128,0.15)',
  },
  stateBlockOff: {
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderColor: 'rgba(255,255,255,0.07)',
  },
  stateLabel: {
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  stateLabelOn: { color: '#4ade80' },
  stateLabelOff: { color: 'rgba(255,255,255,0.3)' },
  stateDesc: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: 12,
    lineHeight: 17,
  },
  impactText: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 11,
    lineHeight: 15,
    fontStyle: 'italic',
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderRadius: 9,
    marginTop: 2,
    borderWidth: 1,
  },
  toggleRowOn: {
    backgroundColor: 'rgba(74,222,128,0.08)',
    borderColor: 'rgba(74,222,128,0.25)',
  },
  toggleRowOff: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderColor: 'rgba(255,255,255,0.1)',
  },
  toggleLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  toggleLabelOn: { color: '#4ade80' },
  toggleLabelOff: { color: 'rgba(255,255,255,0.55)' },
  togglePill: {
    width: 36,
    height: 20,
    borderRadius: 10,
    padding: 2,
    justifyContent: 'center',
  },
  togglePillOn: { backgroundColor: '#4ade80' },
  togglePillOff: { backgroundColor: 'rgba(255,255,255,0.15)' },
  toggleThumbInner: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#fff',
  },
  toggleThumbInnerOn: { alignSelf: 'flex-end' },
  toggleThumbInnerOff: { alignSelf: 'flex-start' },
  footer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    marginTop: 16,
    padding: 12,
    backgroundColor: 'rgba(255,255,255,0.02)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.05)',
  },
  footerText: {
    flex: 1,
    color: 'rgba(255,255,255,0.3)',
    fontSize: 11,
    lineHeight: 16,
  },
  footerCode: {
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier',
    color: 'rgba(255,255,255,0.45)',
  },
});

